import type { LimitField, LimitGroup } from '@contract';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';
import { DataTable } from '@/components/data-table';
import { PageHeader } from '@/components/page-header';
import { StatusPill } from '@/components/primitives/status-pill';
import { OpenRecord } from '@/components/queue/queue-parts';
import { useSheetActions } from '@/components/sheet/use-sheet';
import { Alert } from '@/components/ui/alert';
import { showLimit } from '@/lib/limits';
import { homeCurrency } from '@/lib/market';
import { adminQuery } from '@/lib/query';

export const Route = createFileRoute('/_app/limits')({
  component: LimitsPage,
});

/** The limits the table shows; the rest are in the group's sheet. */
const SUMMARY: readonly { field: string; header: string }[] = [
  { field: 'max_transfer_minor', header: 'Per transfer' },
  { field: 'daily_outflow_minor', header: 'Daily out' },
  { field: 'wallet_daily_outflow_minor', header: 'Wallet daily' },
  { field: 'max_money_out_per_hour', header: 'Money out per hour' },
  { field: 'approval_threshold_minor', header: 'Approval threshold' },
];

const column = createColumnHelper<LimitGroup>();

function columnsFor(fields: readonly LimitField[]) {
  const summary = SUMMARY.flatMap(({ field, header }) => {
    const spec = fields.find((candidate) => candidate.field === field);
    if (!spec) return [];
    return [
      column.display({
        id: field,
        header,
        cell: ({ row }) => {
          const group = row.original;
          // Money limits live in the group's home-currency row, else its first currency.
          const currencyRow =
            group.currencies.find((item) => item.currency === homeCurrency()) ??
            group.currencies[0];
          const source = spec.scope === 'group' ? group : currencyRow;
          return (
            <span className="whitespace-nowrap tabular-nums">
              {showLimit(
                spec,
                source?.[field],
                currencyRow ? String(currencyRow.currency) : undefined,
              )}
            </span>
          );
        },
      }),
    ];
  });
  return [
    column.accessor('name', {
      header: 'Group',
      cell: ({ row }) => (
        <OpenRecord
          sheetRef={{ type: 'limit', id: row.original.code }}
          label={`Open ${row.original.name}`}
        >
          <span className="font-medium">{row.original.name}</span>{' '}
          <span className="rounded-sm bg-muted px-1 font-mono text-xs text-muted-foreground">
            {row.original.code}
          </span>
        </OpenRecord>
      ),
    }),
    column.accessor('is_default', {
      header: 'Default',
      cell: (info) =>
        info.getValue() ? <StatusPill tone="neutral" label="Default" /> : null,
    }),
    column.accessor('customers', { header: 'Customers' }),
    ...summary,
  ];
}

/** Every limit group with its limits; a change is a change request. */
function LimitsPage() {
  const list = useQuery(adminQuery('limits.list', {}));
  const sheetActions = useSheetActions();
  return (
    <>
      <PageHeader
        title="Limits"
        description="The limits of each group, overall and per currency. A change is sent for approval and applies to every customer in the group."
      />
      {list.isError ? (
        <Alert variant="destructive">{list.error.message}</Alert>
      ) : null}
      {list.data ? (
        <DataTable
          id="limit-groups"
          columns={columnsFor(list.data.fields)}
          data={list.data.groups}
          empty="No limit groups."
          rowLabel={(row) => `Open ${row.name}`}
          onRowClick={(row, rowEl) =>
            sheetActions.open(
              { type: 'limit', id: row.code },
              { fromRowEl: rowEl.querySelector('button') ?? rowEl },
            )
          }
        />
      ) : null}
    </>
  );
}
